import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * User preference for automatically fetching full media/files into the local
 * cache when a message renders (so opening is instant), instead of only on
 * click. All OFF by default — auto-download is strictly opt-in, keeping the
 * zero-click parser surface off by default for an E2EE messenger. Thumbnails
 * (and the ThumbHash blur) always render regardless.
 *
 * There is no user-facing size cap: per-type ceilings are baked into config
 * (`AUTO_DOWNLOAD_MAX_BYTES`) — simpler settings and no footgun of enabling
 * auto-download of huge files.
 */
type MediaAutoDownloadState = {
  autoDownloadPhotos: boolean;
  autoDownloadVideos: boolean;
  autoDownloadFiles: boolean;
};

type Actions = {
  setAutoDownloadPhotos: (value: boolean) => void;
  setAutoDownloadVideos: (value: boolean) => void;
  setAutoDownloadFiles: (value: boolean) => void;
};

const initialState: MediaAutoDownloadState = {
  autoDownloadPhotos: false,
  autoDownloadVideos: false,
  autoDownloadFiles: false,
};

export const useMediaAutoDownloadState = create<
  MediaAutoDownloadState & Actions
>()(
  persist(
    (set) => ({
      ...initialState,
      setAutoDownloadPhotos: (value) => set({ autoDownloadPhotos: value }),
      setAutoDownloadVideos: (value) => set({ autoDownloadVideos: value }),
      setAutoDownloadFiles: (value) => set({ autoDownloadFiles: value }),
    }),
    { name: "media-auto-download" },
  ),
);
